import React from "react";
import {
  fitPinLabel,
  normRot,
  visualTopLeftCorner,
} from "./pinLabelFit";

type Props = {
  boothCode: string;
  /** Single name, or multiple lines for a group card. */
  names: string[];
  boxWPct: number;
  boxHPct: number;
  boardW: number;
  boardH: number;
  boardRotation?: number;
  /** Global + per-pin label rotation (name only). */
  nameRotation?: number;
  nameLines?: 1 | 2 | 3 | 4;
  /** ≤1 shrinks auto-fit; never grows past fit. */
  labelScale?: number;
  /** Group rows are buttons in admin. */
  renderName?: (name: string, index: number) => React.ReactNode;
};

/**
 * Booth # overlays the visual top-left (does not reserve layout space).
 * Names are centered in the full card and auto-sized to fit.
 */
export function PinCardLabels({
  boothCode,
  names,
  boxWPct,
  boxHPct,
  boardW,
  boardH,
  boardRotation = 0,
  nameRotation = 0,
  nameLines = 2,
  labelScale = 1,
  renderName,
}: Props) {
  const code = boothCode.trim();
  const multi = names.length > 1;
  const lines = multi ? Math.min(4, Math.max(1, names.length)) : nameLines;
  const nameRot = normRot(nameRotation);
  const nameSwapped = nameRot === 90 || nameRot === 270;
  const nameForFit = names.filter(Boolean).join(" ");
  const { textPx, codePx, pad } = fitPinLabel({
    wPct: boxWPct,
    hPct: boxHPct,
    boardW,
    boardH,
    labelScale,
    nameLines: lines,
    nameRot,
    boothCode: code,
    nameText: nameForFit,
  });
  // Keep code clearly inside the border (border sits on the box edge).
  const inset = Math.max(pad, 3.5);
  const corner = visualTopLeftCorner(boardRotation, inset);

  return (
    <>
      {/* Name — full card center; code does not push it. */}
      <span
        className={`absolute inset-0 overflow-hidden ${renderName ? "" : "pointer-events-none"}`}
        aria-hidden={!renderName}
      >
        <span
          className="absolute flex flex-col items-center justify-center text-center"
          style={{
            left: "50%",
            top: "50%",
            width: nameSwapped ? `${(boxHPct / boxWPct) * 100}%` : "100%",
            height: nameSwapped ? `${(boxWPct / boxHPct) * 100}%` : "100%",
            transform: `translate(-50%, -50%)${nameRot ? ` rotate(${nameRot}deg)` : ""}`,
            padding: inset,
            boxSizing: "border-box",
          }}
        >
          {names.map((n, i) =>
            renderName ? (
              <React.Fragment key={`${i}-${n}`}>{renderName(n, i)}</React.Fragment>
            ) : (
              <span
                key={`${i}-${n}`}
                className="leading-tight text-slate-900 w-full"
                style={{
                  fontSize: multi ? Math.max(4, textPx * 0.92) : textPx,
                  fontWeight: 600,
                  display: multi ? "block" : "-webkit-box",
                  WebkitLineClamp: multi ? undefined : lines,
                  WebkitBoxOrient: multi ? undefined : "vertical",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: multi ? "nowrap" : undefined,
                  overflowWrap: multi ? undefined : "anywhere",
                  wordBreak: multi ? undefined : "break-word",
                  textShadow: "0 0 2px #fff",
                }}
              >
                {n}
              </span>
            ),
          )}
        </span>
      </span>

      {/* Booth # — overlay corner, no layout effect on the name. */}
      {code ? (
        <span
          className="absolute z-[2] leading-none text-slate-900 pointer-events-none"
          style={{
            ...corner,
            fontSize: codePx,
            fontWeight: 600,
            lineHeight: 1,
            maxWidth: "55%",
            overflow: "hidden",
            whiteSpace: "nowrap",
            padding: "1px 2px",
            borderRadius: 2,
            background: "rgba(255,255,255,0.78)",
            textShadow: "none",
            boxSizing: "border-box",
          }}
        >
          {code}
        </span>
      ) : null}
    </>
  );
}
